/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import UiFormInner from 'components/Forms/UIFormInner'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {TicketIcon} from '@heroicons/react/24/outline'
import {zodResolver} from '@hookform/resolvers/zod'
import {OfferEventsInputs, useOfferEventsSchema} from 'components/Offers/schemas/useOfferEventsSchema'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import UiScrollArea from 'components/Ui/UiScrollArea'
import SellerPackagesAction from 'pages/Front/Booking/Seller/SellerPackagesAction'
import SellerPackagesEvent from 'pages/Front/Booking/Seller/SellerPackagesEvent'
import {useForm} from 'react-hook-form'
import BookingAction from 'store/booking/BookingAction'
import {PackageCategoryEntity} from 'types/entities/PackageCategoryEntity'
import {PackageCategory} from 'types/enums/PackageCategory'
import {getOfferEventCategoryShortName} from 'utils/packageUtils'
import {usePackageCategory} from 'utils/hooks/usePackageCategory'
import OfferEventCategoriesList from './OfferEventsFormComponents/OfferEventCategoriesList'

type OfferEventsFormProps = {
    onSuccess: () => void
    onBack?: () => void
}

const OfferEventsForm: React.FC<OfferEventsFormProps> = ({ onSuccess, onBack }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const seller_id = useTypedSelector((state) => state.sessionState.session?.uid)

    const selectedPackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.selectedPackageItems)
    const selectedPackageItemIds = React.useMemo(
        () => selectedPackageItems.map((item) => item.id),
        [selectedPackageItems]
    )

    const filter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)
    const [selectedCategory, setSelectedCategory] = useState<PackageCategory | null>(null)

    const { staticEventsForDisplay} = usePackageCategory()

    const OfferEventsSchema = useOfferEventsSchema()
    const {
        handleSubmit,
        register,
        setValue,
        formState: { errors },
    } = useForm<OfferEventsInputs>({
        resolver: zodResolver(OfferEventsSchema),
        defaultValues: {
            package_item_ids: '',
            adults: filter.adults,
            childrens: filter.children,
        },
    })

    const [isBooking] = useState<boolean>(false)

    const sharedLabelScope = 'OfferEventsForm text-gray-900 dark:text-gray-100'



    useEffect(() => {
        const joined = selectedPackageItemIds.join(',')
        setValue('package_item_ids', joined, { shouldValidate: true })
    }, [selectedPackageItemIds, setValue])

    useEffect(() => {
        setValue('adults', filter.adults)
        setValue('childrens', filter.children)
    }, [filter.adults, filter.children, setValue])

    const isCategoryNotSelected = (categoryId: PackageCategory | string) => {
        if (selectedCategory === null) {
            return null
        }
        return selectedCategory !== categoryId
    }

    const selectEventCategoryHandler = (eventCategory: PackageCategoryEntity) => {
        dispatch(BookingAction.clearAvailablePackagesItems())
        dispatch(
            BookingAction.setFilter({
                ...filter,
                category: eventCategory.id,
            })
        )
        setSelectedCategory(eventCategory.id)
    }

    const onSubmit = useCallback(
        async () => {
            if (!seller_id) {
                return
            }

            onSuccess()
        },
        [onSuccess, seller_id]
    )

    const renderCategoryContent = (eventCategory: PackageCategoryEntity, disabled: boolean) => {
        return (
            <>
                {isCategoryNotSelected(eventCategory.id) === false && (
                    <UiScrollArea
                        size={'sm'}
                        textSize={'sm'}
                        maxHeightClass={'max-h-[68vh]'}
                        className={clsx('px-3 xl:px-4 pb-2.5 lg:pb-3 xl:pb-4')}
                    >
                        {eventCategory.id === PackageCategory.School ||
                        eventCategory.id === PackageCategory.MegaFunSchool ||
                        eventCategory.id === PackageCategory.SchoolTrophy ||
                        eventCategory.id === PackageCategory.Kinder ? (
                            <SellerPackagesAction
                                packageCategory={eventCategory.id}
                                onContinue={handleSubmit(onSubmit)}
                                onBack={onBack}
                                isBooking={isBooking}
                            />
                        ) : (
                            <SellerPackagesEvent
                                packageCategory={eventCategory.id}
                                onContinue={handleSubmit(onSubmit)}
                                onBack={onBack}
                                isBooking={isBooking}
                            />
                        )}
                    </UiScrollArea>
                )}

                {isCategoryNotSelected(eventCategory.id) === null && (
                    <div className={'grow flex flex-col justify-end'}>
                        <div className={clsx('-mt-px flex')}>
                            <div className={'flex w-0 flex-1 p-2 bg-gray-50 dark:bg-gray-800/50'}>
                                <UiButton
                                    style={UiButtonStyle.Primary}
                                    size={UiButtonSize.Large}
                                    disabled={disabled}
                                    className={clsx(
                                        'relative inline-flex w-full items-center justify-center gap-x-3 rounded-lg py-2.5 text-sm font-semibold shadow-sm'
                                    )}
                                    onClick={() => selectEventCategoryHandler(eventCategory)}
                                    icon={<TicketIcon className={'size-5'} />}
                                >
                                    <span>{t('Select')}</span>
                                </UiButton>
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={clsx('OfferEventsForm space-y-6', sharedLabelScope)}>
            <UiFormInner>
                <input type={'hidden'} {...register('package_item_ids')} />
                {errors.package_item_ids && (
                    <div className={clsx('text-red-500 text-xs')}>{errors.package_item_ids.message}</div>
                )}
                <div className={clsx('md:col-span-2 space-y-4')}>
                    <OfferEventCategoriesList
                        eventCategories={staticEventsForDisplay}
                        getShortNameFromId={getOfferEventCategoryShortName}
                        isCategoryNotSelected={isCategoryNotSelected}
                        renderCategoryContent={renderCategoryContent}
                    />
                </div>
            </UiFormInner>
        </div>
    )
}

export default OfferEventsForm
